import { ReactElement, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths } from 'configuration/paths';
import { getFullstoryProperties } from 'utils/property';
import { scrollToTop } from 'utils/scroll';
import { TranslationsKeys } from 'types/translation';
import { Survey as SurveyType } from 'types/survey';
import { useTranslate } from 'hooks/useTranslate';
import { useSurveyDefinitionQuery, useSurveyResponseQuery } from 'hooks/queries';
import { useDeleteSurveyResponse } from 'hooks/mutations';
import { SurveyHeader } from 'components/SurveyHeader';
import { Footer } from 'components/Footer';
import { Button } from 'components/ui/Button';
import { SurveyStepper, SurveyStepperSteps } from 'components/SurveyStepper';
import { SurveySectionList } from 'components/SurveySectionList';
import { sortSurvey } from 'utils/survey';
import styles from './Survey.module.scss';

const Survey = (): ReactElement => {
  const { t } = useTranslate();
  const { surveyId } = useParams();
  const surveyResponseQuery = useSurveyResponseQuery(surveyId);
  const surveyResponse = surveyResponseQuery.data;
  const surveyDefinitionQuery = useSurveyDefinitionQuery(
    surveyResponse ? surveyResponse.surveyDefinitionId : undefined
  );
  const surveyDefinition = useMemo(
    () => (surveyDefinitionQuery.data ? sortSurvey(surveyDefinitionQuery.data) : undefined),
    [surveyDefinitionQuery.data]
  ) as SurveyType;
  const isLoading = surveyResponseQuery.isLoading || surveyDefinitionQuery.isLoading;
  const isError = surveyResponseQuery.isError || surveyDefinitionQuery.isError;
  const navigate = useNavigate();
  const deleteSurveyResponseMutation = useDeleteSurveyResponse();

  const onSelect = (sectionId: string, subsectionId: string) => {
    navigate(
      paths.surveySection
        .replace(':surveyId', surveyResponse!.responseId)
        .replace(':sectionId', sectionId)
        .replace(':subsectionId', subsectionId)
    );
    scrollToTop();
  };

  const deleteSurvey = async () => {
    if (!surveyResponse) {
      console.warn('Survey response not found!');
      return;
    }

    try {
      await deleteSurveyResponseMutation.mutateAsync(surveyResponse);
      navigate(paths.mySurveys);
    } catch (error) {
      console.error('Error deleting survey response:', error);
    }
  };

  return (
    <div {...getFullstoryProperties(surveyDefinition)}>
      <SurveyHeader
        isLoading={isLoading}
        isError={isError}
        surveyDefinition={surveyDefinition}
        surveyResponse={surveyResponse ?? undefined}
        onDelete={deleteSurvey}
      />

      <div className={styles.surveyIntro}>
        <SurveyStepper activeStep={SurveyStepperSteps.Survey} surveyResponse={surveyResponse} />
        {surveyDefinition && surveyResponse && (
          <div className={styles.survey}>
            <SurveySectionList surveyDefinition={surveyDefinition} surveyResponse={surveyResponse} onSelect={onSelect} />
          </div>
        )}
      </div>

      <Footer className={styles.surveyIntroFooter}>
        <Button
          onClick={() => navigate(paths.surveyWrapUp.replace(':surveyId', surveyId!))}
          className={styles.surveyFooterButton}>
          {t(TranslationsKeys.surveyPage_continue)}
        </Button>
      </Footer>
    </div>
  );
};

export default Survey;
